import { Suspense, lazy } from 'react';
import { useT } from '@/i18n/use-t';
import { useOpenApiDocument } from '@/hooks/use-openapi';

/**
 * The API reference, for anyone logged in to this tenant (RL-385).
 *
 * The renderer and its stylesheet live in `api-docs-viewer.tsx` and are loaded
 * lazily. They are far larger than the rest of this application put together,
 * and the admin bundle already trips Vite's 500 kB chunk warning — loading them
 * eagerly would make every page pay for one most people never open. The
 * stylesheet has to travel with the renderer: without it Swagger UI renders as
 * unstyled plain text, which is what shipped the first time.
 *
 * The spec arrives through `apiClient`, so it carries the user's own bearer
 * token. That is what makes this page the tenant's door to the docs: the `/docs`
 * Swagger UI that BOS mounts sits outside Nest's guards and is gated at the edge
 * by a shared operator credential, which is the wrong key to hand a retailer.
 */
const ApiDocsViewer = lazy(() => import('./api-docs-viewer'));

export function ApiDocsPage() {
  const t = useT('admin_ui');
  const { data, isLoading, isError, error } = useOpenApiDocument();

  const notPublished =
    isError && (error as { response?: { status?: number } })?.response?.status === 404;

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t('apiDocs.title', 'API reference')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'apiDocs.description',
            'The REST API of this tenant, generated from the running build. Calls you send from here use your own session and touch this tenant’s data.',
          )}
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : notPublished ? (
        <div className="rounded-md border border-border bg-card p-4">
          <p className="text-sm">
            {t(
              'apiDocs.notPublished',
              'This deployment does not publish the API document. An operator can enable it.',
            )}
          </p>
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {t('apiDocs.loadError', 'Could not load the API document.')}
        </p>
      ) : data === undefined ? null : (
        <div className="overflow-hidden rounded-md border border-border">
          <Suspense
            fallback={
              <p className="p-4 text-sm text-muted-foreground">
                {t('apiDocs.rendererLoading', 'Loading the API browser…')}
              </p>
            }
          >
            <ApiDocsViewer spec={data} />
          </Suspense>
        </div>
      )}
    </div>
  );
}
